<template>
  <QuizFrame
    :question-index="questionIndex"
    :total-questions="totalQuestions"
    :can-go-back="canGoBack"
    :is-last="isLast"
    :primer-label="primerLabel"
    :primer-content="primerContent"
    :locale="locale"
    @back="$emit('back')"
    @next="$emit('next')"
  >
    <div class="question-block">
      <p v-if="sectionLabel" class="section-label">{{ sectionLabel }}</p>
      <h1 class="question-title" :class="{ 'is-placeholder': isTitlePlaceholder }">
        {{ displayTitle }}
      </h1>
      <p v-if="displayPrompt" class="question-prompt">{{ displayPrompt }}</p>
    </div>

    <button
      v-if="tooltipLabelText"
      type="button"
      class="tooltip-button"
      :aria-expanded="String(tooltipOpen)"
      @click="tooltipOpen = !tooltipOpen"
    >
      <span class="tooltip-icon" aria-hidden="true">i</span>
      {{ tooltipLabelText }}
    </button>

    <div
      v-if="tooltipLabelText && tooltipOpen"
      class="tooltip-content"
    >
      {{ tooltipBodyText }}
    </div>

    <fieldset class="answer-fieldset">
      <legend class="sr-only">{{ choiceChrome.answerOptionsLegend }}</legend>
      <label
        v-for="opt in displayOptions"
        :key="opt.id"
        class="answer-option"
        :class="{
          'is-selected': selectedId === opt.id,
          'is-placeholder': opt.isPlaceholder,
        }"
      >
        <input
          type="radio"
          :name="radioGroupName"
          :value="opt.id"
          v-model="selectedId"
        >
        <span class="answer-option-label">{{ opt.displayLabel }}</span>
      </label>
    </fieldset>
  </QuizFrame>
</template>

<script>
import QuizFrame from './QuizFrame.vue';

// Localized strings specific to the single-choice body: placeholders for
// the editor preview when fields are empty, and the screen-reader-only
// legend on the radio fieldset. Chrome strings live in QuizFrame.
const CHOICE_DICT = {
  en: {
    placeholderTitle: 'Question title appears here',
    placeholderOption: 'Answer option',
    answerOptionsLegend: 'Answer options',
  },
  de: {
    placeholderTitle: 'Frage-Titel erscheint hier',
    placeholderOption: 'Antwortoption',
    answerOptionsLegend: 'Antwortoptionen',
  },
  fr: {
    placeholderTitle: 'Le titre de la question apparaît ici',
    placeholderOption: 'Option de réponse',
    answerOptionsLegend: 'Options de réponse',
  },
  it: {
    placeholderTitle: 'Il titolo della domanda appare qui',
    placeholderOption: 'Opzione di risposta',
    answerOptionsLegend: 'Opzioni di risposta',
  },
};

// Unique radio-group name per component instance so multiple previews
// (e.g. in a list view) don't share selection state.
let renderInstance = 0;

export default {
  name: 'SingleChoiceRenderer',
  components: { QuizFrame },
  props: {
    title: { type: Object, default: () => ({}) },
    prompt: { type: Object, default: () => ({}) },
    // Inline info button below the title — distinct from the header
    // primer (which is driven by help_text → primerContent). When the
    // active locale has no label, the button doesn't render.
    tooltipLabel: { type: Object, default: () => ({}) },
    tooltipBody: { type: Object, default: () => ({}) },
    // Array of { id, label: i18n, score } per SPEC_SINGLE_CHOICE_SCHEMA.md.
    options: { type: Array, default: () => [] },
    locale: { type: String, default: 'de' },
    sectionLabel: { type: String, default: '' },
    questionIndex: { type: Number, default: 0 },
    totalQuestions: { type: Number, default: 0 },
    canGoBack: { type: Boolean, default: false },
    isLast: { type: Boolean, default: false },
    primerLabel: { type: String, default: 'Info' },
    primerContent: { type: String, default: '' },
    // Show placeholder text where required fields are still empty.
    preview: { type: Boolean, default: false },
  },
  emits: ['back', 'next'],
  data() {
    renderInstance += 1;
    return {
      radioGroupName: `sc-radio-${renderInstance}`,
      tooltipOpen: false,
      selectedId: null,
    };
  },
  computed: {
    titleText() {
      return (this.title && this.title[this.locale]) || '';
    },
    promptText() {
      return (this.prompt && this.prompt[this.locale]) || '';
    },
    tooltipLabelText() {
      return ((this.tooltipLabel && this.tooltipLabel[this.locale]) || '').trim();
    },
    tooltipBodyText() {
      return ((this.tooltipBody && this.tooltipBody[this.locale]) || '').trim();
    },
    choiceChrome() {
      return CHOICE_DICT[this.locale] || CHOICE_DICT.en;
    },
    displayTitle() {
      if (this.titleText.trim()) return this.titleText;
      return this.preview ? this.choiceChrome.placeholderTitle : '';
    },
    displayPrompt() {
      return this.promptText.trim();
    },
    isTitlePlaceholder() {
      return !this.titleText.trim();
    },
    // Decorate each option with its locale-resolved label and a flag
    // for whether the label is empty in the active locale (preview
    // shows a placeholder instead of a blank radio).
    displayOptions() {
      const placeholder = this.choiceChrome.placeholderOption;
      return (this.options || []).map((opt, idx) => {
        const raw = ((opt.label && opt.label[this.locale]) || '').trim();
        const isPlaceholder = !raw;
        return {
          id: opt.id != null ? opt.id : `opt_${idx + 1}`,
          displayLabel: raw
            || (this.preview ? `${placeholder} ${idx + 1}` : ''),
          isPlaceholder,
        };
      });
    },
  },
  watch: {
    tooltipLabelText(newVal) {
      // If the active-locale label disappears (e.g. user clears it in
      // the editor or switches to a tab where it's empty), close the
      // popover so the renderer doesn't leave orphaned UI showing.
      if (!newVal) this.tooltipOpen = false;
    },
  },
};
</script>

<style scoped>
.question-block {
  margin-bottom: 1rem;
}

.section-label {
  font-size: 13px;
  color: #5f5e5a;
  margin: 0 0 6px;
}

.question-title {
  font-size: 22px;
  font-weight: 500;
  margin: 0 0 8px;
  line-height: 1.25;
  color: #2c2c2a;
}

.question-title.is-placeholder {
  color: #888780;
  font-style: italic;
}

.question-prompt {
  font-size: 14px;
  color: #5f5e5a;
  margin: 0;
}

/* Inline tooltip button above the answer options. Distinct from the
   header primer button (which lives in QuizFrame and uses a modal). */
.tooltip-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #EEEDFE;
  color: #534AB7;
  border: 0.5px solid #534AB7;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0.75rem;
}

.tooltip-button:hover {
  background: #534AB7;
  color: white;
}

.tooltip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #534AB7;
  color: white;
  font-size: 10px;
  font-style: italic;
  font-family: Georgia, serif;
  line-height: 1;
}

.tooltip-button:hover .tooltip-icon {
  background: white;
  color: #534AB7;
}

.tooltip-content {
  font-size: 13px;
  color: #2c2c2a;
  background: #fafaf7;
  border-left: 3px solid #534AB7;
  padding: 10px 12px;
  margin: 0 0 1rem;
  line-height: 1.5;
  border-radius: 0 8px 8px 0;
}

.answer-fieldset {
  border: none;
  padding: 0;
  margin: 0 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.answer-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 0.5px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  font-size: 14px;
  color: #2c2c2a;
}

.answer-option:hover {
  background: #fafaf7;
  border-color: #534AB7;
}

.answer-option.is-selected {
  background: #EEEDFE;
  border-color: #534AB7;
}

.answer-option input {
  accent-color: #534AB7;
  cursor: pointer;
  flex-shrink: 0;
}

.answer-option-label {
  line-height: 1.4;
}

.answer-option.is-placeholder .answer-option-label {
  color: #888780;
  font-style: italic;
}
</style>
